/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type {
  BillingHistory,
  Prepaids,
  AzureMarketplaceSubscription,
} from '@modules/cloud-api/v1/types'

import { FETCH_ORGANIZATION_PREPAIDS, FETCH_MARKETPLACE_BILLING_DETAILS } from '@/constants/actions'
import { replaceIn } from '@/lib/immutability-helpers'

import type { ReduxState } from '@/types/redux'

export type UserState = {
  billingHistory?: BillingHistory
  prepaids?: Prepaids
  marketplaceBillingDetails?: AzureMarketplaceSubscription // for now we have only details for Azure
}

export type State = Record<string, UserState>

const initialState: State = {}

export default function billingReducer(state = initialState, action): State {
  if (!action.error && action.payload) {
    if (action.type === FETCH_MARKETPLACE_BILLING_DETAILS) {
      const {
        payload,
        meta: { organizationId },
      } = action

      return replaceIn(state, [organizationId, 'marketplaceBillingDetails'], payload)
    }
  }

  if (action.type === FETCH_ORGANIZATION_PREPAIDS) {
    const {
      payload,
      meta: { organizationId },
    } = action

    return replaceIn(state, [organizationId, 'prepaids'], payload)
  }

  return state
}

function _getOrganizationPrepaids(state: State, organizationId: string): Prepaids | undefined {
  return state[organizationId]?.prepaids
}

function _getMarketplaceBillingDetails(
  state: State,
  organizationId: string,
): AzureMarketplaceSubscription | undefined {
  return state[organizationId]?.marketplaceBillingDetails
}

export const getOrganizationPrepaids = (state: ReduxState, organizationId: string) =>
  _getOrganizationPrepaids(state.billing, organizationId)

export const getMarketplaceBillingDetails = (state: ReduxState, organizationId: string) =>
  _getMarketplaceBillingDetails(state.billing, organizationId)
